import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { SubmitHandler, useForm } from 'react-hook-form';
import { SelectInstance } from 'react-select';
import { isAxiosError } from 'axios';
import { toast } from 'react-toastify';
import { CreateLotacaoDto, ReadLotacaoDto, ReadSetorDto } from '@/api.types';
import { EditLotacaoCard } from '@/modules/business/lotacoes/editLotacaoCard';
import { TipoLotacao } from '@/modules/business/lotacoes/enums/tipo-lotacao.enum';
import { TipoPessoaLotada } from '@/modules/business/lotacoes/enums/tipo-pessoa-lotada.enum';
import {
  IOpcaoPredio,
  useCarregarDadosIniciais,
} from '@/modules/business/lotacoes/hooks/useDadosVinculo';
import { LotacaoService } from '@/modules/business/lotacoes/lotacao.service';
import ModalConflitos, {
  ValidacaoConflitos,
} from '@/modules/business/lotacoes/ModalConflitos';
import '@/modules/business/lotacoes/lotacao-form-abas.css';
import '@/modules/people/presentation/components/perfil-tabs-acoes.css';
import { BotaoAcaoPerfil } from '@/modules/people/presentation/components/BotaoAcaoPerfil';
import { readEstagiario } from '@/modules/people/estagiario.service';
import Loading from '@/shared/components/Carregando';
import ModalExcluir from '@/shared/components/modals/ModalExcluir';
import { IOpcao, SelectOption } from '@/shared/types/selectOption';
import { DataAmigavel, DataIso } from '@/shared/utils/datas';

export type TipoPessoaLotacaoModal = 'defensor' | 'colaborador' | 'estagiario';

type AdicionarEditarLotacaoModalProps = {
  show: boolean;
  handleHide: () => void;
  tipoPessoa: TipoPessoaLotacaoModal;
  pessoaLotadaId: number;
  lotacaoId: number | null;
  onSalvo: () => void;
};

function mapTipoPessoa(tipo: TipoPessoaLotacaoModal): TipoPessoaLotada {
  if (tipo === 'colaborador') return TipoPessoaLotada.Colaborador;
  if (tipo === 'estagiario') return TipoPessoaLotada.Estagiario;
  return TipoPessoaLotada.Defensor;
}

function tipoPadrao(tipo: TipoPessoaLotacaoModal): TipoLotacao {
  if (tipo === 'defensor') return TipoLotacao.Titularidade;
  return TipoLotacao.Designacao;
}

export default function AdicionarEditarLotacaoModal({
  show,
  handleHide,
  tipoPessoa,
  pessoaLotadaId,
  lotacaoId,
  onSalvo,
}: AdicionarEditarLotacaoModalProps) {
  const lotacaoService = useMemo(() => new LotacaoService(), []);
  const tipoPessoaLotada = mapTipoPessoa(tipoPessoa);
  const idEdicao = lotacaoId && lotacaoId > 0 ? lotacaoId : 0;

  const { register, handleSubmit, reset, getValues, setValue } = useForm<ReadLotacaoDto>();
  const [registro, setRegistro] = useState<ReadLotacaoDto>();
  const [isLoading, setIsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [modalConflitos, setModalConflitos] = useState(false);
  const [conflitos, setConflitos] = useState<ValidacaoConflitos | null>(null);
  const [modalExcluirShow, setModalExcluirShow] = useState(false);

  const refSelectColaborador = useRef<SelectInstance<IOpcao | null>>(null);
  const refSelectDefensor = useRef<SelectInstance<IOpcao | null>>(null);
  const refSelectDefensoria = useRef<SelectInstance<IOpcao | null>>(null);
  const refSelectEstagiario = useRef<SelectInstance<IOpcao | null>>(null);
  const refSelectEstagio = useRef<SelectInstance<IOpcao | null>>(null);
  const refSelectNucleo = useRef<SelectInstance<IOpcao | null>>(null);
  const refSelectPredio = useRef<SelectInstance<IOpcao | null>>(null);
  const refSelectSetor = useRef<SelectInstance<IOpcao | null>>(null);
  const refSelectResponsavel = useRef<SelectInstance<SelectOption | null>>(null);

  const [colaboradores, setColaboradores] = useState<IOpcao[]>([]);
  const [defensores, setDefensores] = useState<IOpcao[]>([]);
  const [defensorias, setDefensorias] = useState<IOpcao[]>([]);
  const [estagiarios, setEstagiarios] = useState<IOpcao[]>([]);
  const [pessoas, setPessoas] = useState<SelectOption[]>([]);
  const [estagios, setEstagios] = useState<IOpcao[]>([]);
  const [nucleos, setNucleos] = useState<IOpcao[]>([]);
  const [predios, setPredios] = useState<IOpcaoPredio[]>([]);
  const [setoresDisponiveis, setSetoresDisponiveis] = useState<IOpcao[]>([]);

  const handleSelectChangeEstagiario = useCallback(
    async (idEstagiario: number) => {
      if (refSelectEstagio.current) {
        refSelectEstagio.current.setValue(null, 'deselect-option');
      }

      if (idEstagiario > 0) {
        try {
          const res = await readEstagiario({
            id: idEstagiario,
            skip: 0,
            take: 1,
            comEstagios: true,
          });
          if (res[0][0]?.estagios) {
            const listaEstagios = res[0][0].estagios.map((item) => ({
              value: item.id,
              label: `${DataAmigavel(item.data_inicio_estagio)} - ${TipoLotacao[item.tipo_estagio]}`,
            }));
            setEstagios(listaEstagios);
          }
        } catch (err) {
          console.warn(err);
        }
      } else {
        setEstagios([]);
        refSelectEstagio.current?.setValue(null, 'deselect-option');
      }
    },
    [],
  );

  const preencherPessoaFixa = useCallback(async () => {
    if (tipoPessoa === 'defensor') {
      setValue('defensor', { id: pessoaLotadaId, ativo: true });
      setValue('colaborador', undefined);
      setValue('estagio', undefined);
    } else if (tipoPessoa === 'colaborador') {
      setValue('colaborador', { id: pessoaLotadaId, ativo: true });
      setValue('defensor', undefined);
      setValue('estagio', undefined);
    } else {
      setValue('defensor', undefined);
      setValue('colaborador', undefined);
      await handleSelectChangeEstagiario(pessoaLotadaId);
    }
  }, [handleSelectChangeEstagiario, pessoaLotadaId, setValue, tipoPessoa]);

  const buscar = useCallback(async () => {
    if (idEdicao > 0) {
      try {
        const result = await lotacaoService.Read(idEdicao);
        if (result) {
          result.data_inicial = DataIso(result.data_inicial);
          result.data_final = DataIso(result.data_final);

          if (result.estagio?.estagiario) {
            await handleSelectChangeEstagiario(result.estagio.estagiario.id!);
          }

          setRegistro(result);
          reset(result);
        }
      } catch (res) {
        toast.error((res as string) + '');
      }
    } else {
      setRegistro(undefined);
      reset({
        ativo: true,
        tipo: tipoPadrao(tipoPessoa),
      } as ReadLotacaoDto);
      await preencherPessoaFixa();
    }
  }, [
    handleSelectChangeEstagiario,
    idEdicao,
    lotacaoService,
    preencherPessoaFixa,
    reset,
    tipoPessoa,
  ]);

  useCarregarDadosIniciais({
    buscar,
    setColaboradores,
    setDefensores,
    setDefensorias,
    setEstagiarios,
    setPessoas,
    setLoaded,
    setNucleos,
    setPredios,
    setSetoresDisponiveis,
  });

  useEffect(() => {
    if (predios.length > 0 && registro?.setor?.predio?.id) {
      const predioId = registro.setor.predio.id;
      const predioSelecionado = predios.find((predio) => predio.value === predioId);

      if (predioSelecionado?.setores) {
        const setoresFiltrados = predioSelecionado.setores.map(
          (setor: { id: number; nome: string }) => ({
            value: setor.id,
            label: setor.nome,
          }),
        );
        setSetoresDisponiveis(setoresFiltrados);

        if (refSelectPredio.current) {
          const predioOption = predios.find((p) => p.value === predioId);
          if (predioOption) {
            refSelectPredio.current.setValue(predioOption, 'select-option');
          }
        }

        if (registro.setor?.id && refSelectSetor.current) {
          const setorSelecionado = setoresFiltrados.find((s) => s.value === registro.setor?.id);
          if (setorSelecionado) {
            refSelectSetor.current.setValue(setorSelecionado, 'select-option');
          }
        }
      }
    } else if (predios.length > 0 && !registro?.setor?.predio?.id) {
      setSetoresDisponiveis([]);
    }
  }, [predios, registro]);

  useEffect(() => {
    if (estagios.length > 0) {
      const currentEstagioId = getValues('estagio')?.id;
      if (currentEstagioId) {
        const selected = estagios.find((e) => e.value === currentEstagioId);
        if (selected && refSelectEstagio.current) {
          refSelectEstagio.current.setValue(selected, 'select-option');
        }
      }
    }
  }, [estagios, getValues]);

  const handleSelectChangePessoa = useCallback(
    async (
      selectedOption: IOpcao | null,
      options: IOpcao[],
      field: 'defensor' | 'colaborador' | 'estagiario' | 'estagio',
    ) => {
      if (selectedOption) {
        const selectedItem = options.find((option) => option.value === selectedOption.value);
        if (!selectedItem) return;

        if (field === 'estagiario') {
          await handleSelectChangeEstagiario(selectedItem.value);
          return;
        }

        if (field === 'estagio') {
          const currentEstagio = getValues('estagio');
          let estagiarioToPreserve = currentEstagio?.estagiario;
          if (!estagiarioToPreserve && pessoaLotadaId > 0) {
            const estagiarioOption = estagiarios.find((e) => e.value === pessoaLotadaId);
            estagiarioToPreserve = {
              id: pessoaLotadaId,
              ativo: true,
              ...(estagiarioOption
                ? { pessoa: { id: '', nome: estagiarioOption.label } }
                : {}),
            };
          }

          if (!estagiarioToPreserve) {
            toast.warning('Não foi possível vincular o estágio: estagiário não identificado.');
            return;
          }

          setValue('estagio', {
            id: selectedItem.value,
            estagiario: estagiarioToPreserve,
          });
          return;
        }

        setValue(field as 'defensor' | 'colaborador', {
          id: selectedItem.value,
          ativo: true,
          pessoa: { id: '', nome: selectedItem.label },
        });
      } else if (field === 'estagio') {
        setValue('estagio', undefined);
      } else if (field !== 'estagiario') {
        setValue(field, undefined);
      }
    },
    [estagiarios, getValues, handleSelectChangeEstagiario, pessoaLotadaId, setValue],
  );

  const handleSelectChangeLocal = useCallback(
    (
      selectedOption: IOpcao | null,
      options: IOpcao[],
      field: 'defensoria' | 'nucleo' | 'predio' | 'setor',
    ) => {
      if (field === 'predio') {
        const predioSelecionado = selectedOption
          ? predios.find((predio) => predio.value === selectedOption.value)
          : null;

        let setoresAssociados: ReadSetorDto[] = [];
        if (predioSelecionado?.setores) {
          setoresAssociados = predioSelecionado.setores;
        }

        setSetoresDisponiveis(
          setoresAssociados.map((setor: { id: number; nome: string }) => ({
            value: setor.id,
            label: setor.nome,
          })),
        );

        if (refSelectSetor.current) {
          refSelectSetor.current.setValue(null, 'deselect-option');
        }
        setValue('setor', undefined);
        return;
      }

      if (selectedOption) {
        const selectedItem = options.find((option) => option.value === selectedOption.value);
        if (selectedItem) {
          const newValue = { id: selectedItem.value, nome: selectedItem.label };
          setValue(field as 'defensoria' | 'nucleo' | 'setor', newValue);
          if (field === 'defensoria') {
            if (refSelectNucleo.current) refSelectNucleo.current.setValue(null, 'deselect-option');
            if (refSelectSetor.current) refSelectSetor.current.setValue(null, 'deselect-option');
            if (refSelectPredio.current) refSelectPredio.current.setValue(null, 'deselect-option');
            setValue('nucleo', undefined);
            setValue('setor', undefined);
          } else if (field === 'nucleo') {
            if (refSelectDefensoria.current) {
              refSelectDefensoria.current.setValue(null, 'deselect-option');
            }
            setValue('defensoria', undefined);
          }
        }
      } else {
        setValue(field as 'defensoria' | 'nucleo' | 'setor', undefined);
      }
    },
    [predios, setValue],
  );

  const handleSelectChangeDados = useCallback(
    (
      selectedOption: SelectOption | null,
      options: SelectOption[],
      field: 'responsavel',
    ) => {
      if (selectedOption) {
        const selectedItem = options.find((option) => option.value === selectedOption.value);
        if (selectedItem) {
          setValue(field, { id: selectedItem.value, nome: selectedItem.label });
        }
      } else {
        setValue(field, undefined);
      }
    },
    [setValue],
  );

  const onSubmit: SubmitHandler<CreateLotacaoDto> = async (obj) => {
    if (!obj.setor) obj.setor = undefined;

    if (obj.defensoria?.id) {
      delete obj.nucleo;
    } else if (obj.nucleo?.id) {
      delete obj.defensoria;
    }

    if ('predio' in obj) {
      delete (obj as { predio?: unknown }).predio;
    }

    //@ts-expect-error ts2322
    obj.data_inicial =
      obj.data_inicial && obj.data_inicial?.length >= 10 ? DataIso(obj.data_inicial) : null;
    //@ts-expect-error ts2322
    obj.data_final =
      obj.data_final && obj.data_final?.length >= 10 ? DataIso(obj.data_final) : null;

    if (obj.entrada === '') delete obj.entrada;
    if (obj.saida === '') delete obj.saida;

    if (tipoPessoa === 'defensor') {
      obj.defensor = { id: pessoaLotadaId };
      delete obj.colaborador;
      delete obj.estagio;
    } else if (tipoPessoa === 'colaborador') {
      obj.colaborador = { id: pessoaLotadaId };
      delete obj.defensor;
      delete obj.estagio;
    } else {
      delete obj.defensor;
      delete obj.colaborador;
      if (obj.estagio?.id) {
        obj.estagio = { id: obj.estagio.id };
      }
    }

    setIsLoading(true);
    try {
      if (idEdicao > 0) {
        const res = await lotacaoService.Update(obj, idEdicao);
        if (res === true) {
          toast.success('Registro atualizado com sucesso.');
          onSalvo();
          handleHide();
        } else {
          toast.warning(res);
        }
      } else {
        const res = await lotacaoService.Create(obj);
        if (!isNaN(res as number)) {
          toast.success('Registro salvo com sucesso.');
          onSalvo();
          handleHide();
        } else {
          toast.warning(res);
        }
      }
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 400) {
        const errorData = error.response.data;
        if (errorData?.conflitos && Array.isArray(errorData.conflitos)) {
          setConflitos(errorData);
          setModalConflitos(true);
          return;
        }
      }
      toast.error((error as string) + '');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSalvar = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    void handleSubmit(onSubmit)();
  };

  const handleExcluir = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (idEdicao <= 0) return;

    setModalExcluirShow(false);
    setIsLoading(true);
    lotacaoService
      .Delete(idEdicao)
      .then(() => {
        toast.success('Lotação excluída com sucesso.');
        onSalvo();
        handleHide();
      })
      .catch((res) => {
        toast.error((res as string) + '');
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleHide}
        size="lg"
        centered
        backdrop="static"
        scrollable
        dialogClassName="lotacao-modal-dialog"
      >
        <Modal.Header closeButton className="py-2 perfil-modal-header">
          <Modal.Title className="fs-6">
            {idEdicao > 0 ? 'Editar lotação' : 'Nova lotação'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-3">
          {!loaded ? (
            <Loading />
          ) : (
            <EditLotacaoCard
              form={{ register, getValues }}
              tipoPessoaLotada={tipoPessoaLotada}
              ocultarSelecaoPessoa
              layout="abas"
              pessoa={{
                colaboradores,
                defensores,
                estagiarioid: tipoPessoa === 'estagiario' ? pessoaLotadaId : 0,
                estagiarios,
                estagioid: 0,
                estagios,
                refSelectColaborador,
                refSelectDefensor,
                refSelectEstagiario,
                refSelectEstagio,
                handleSelectChange: handleSelectChangePessoa,
              }}
              dados={{
                pessoas,
                refSelectResponsavel,
                handleSelectChange: handleSelectChangeDados,
              }}
              local={{
                defensorias,
                nucleos,
                predios: predios as unknown as IOpcao[],
                setoresDisponiveis,
                refSelectDefensoria,
                refSelectNucleo,
                refSelectPredio,
                refSelectSetor,
                handleSelectChange: handleSelectChangeLocal,
              }}
            />
          )}
        </Modal.Body>
        <Modal.Footer className="py-2 d-flex justify-content-between">
          <div>
            {idEdicao > 0 && (
              <BotaoAcaoPerfil
                tipo="excluir"
                disabled={!loaded || isLoading}
                onClick={() => setModalExcluirShow(true)}
              />
            )}
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" size="sm" type="button" onClick={handleHide}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="button"
              className="perfil-modal-btn-salvar"
              disabled={!loaded || isLoading}
              onClick={handleSalvar}
            >
              {isLoading ? 'Salvando...' : idEdicao > 0 ? 'Salvar' : 'Cadastrar'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <ModalExcluir
        show={modalExcluirShow}
        handleHide={() => setModalExcluirShow(false)}
        handleExcluir={handleExcluir}
        mensagemPersonalizada="Deseja excluir esta lotação?"
      />

      <ModalConflitos
        show={modalConflitos}
        handleHide={() => setModalConflitos(false)}
        conflitos={conflitos}
      />
    </>
  );
}
